/* antarti — global bootstrap. Loaded with `defer` on every page.
   Motion libraries (GSAP, ScrollTrigger, Lenis) are wired here from S04 onward;
   until then this file only provides shared helpers and CSS-driven primitives. */

(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const config = readJSON('antarti-config') || {};

  window.antarti = {
    config,
    get reducedMotion() {
      return reduceMotion.matches;
    },
    define,
    readJSON,
  };

  /** Registers a custom element once, even if the script is evaluated twice (theme editor). */
  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /** Parses a `<script type="application/json" id="…">` block. */
  function readJSON(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /* Aurora: pause drift while a section is off screen. */
  const auroraObserver = new IntersectionObserver(
    (entries) => entries.forEach((e) => e.target.classList.toggle('is-offscreen', !e.isIntersecting)),
    { rootMargin: '10% 0px' }
  );

  /* Glint on touch devices: one sweep when 60% visible, another on tap. */
  const hoverCapable = window.matchMedia('(hover: hover)').matches;
  const glintObserver = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          sweep(e.target);
          glintObserver.unobserve(e.target);
        }
      }),
    { threshold: [0.6] }
  );

  function sweep(el) {
    if (reduceMotion.matches) return;
    el.classList.remove('is-glinting');
    void el.offsetWidth; // restart the animation
    el.classList.add('is-glinting');
  }

  function observe(root = document) {
    root.querySelectorAll('.u-aurora').forEach((el) => auroraObserver.observe(el));
    if (!hoverCapable) {
      root.querySelectorAll('.u-glint').forEach((el) => {
        glintObserver.observe(el);
        el.addEventListener('pointerdown', () => sweep(el), { passive: true });
      });
    }
  }

  observe();

  /* Theme editor: re-observe when a section is (re)loaded. */
  document.addEventListener('shopify:section:load', (e) => observe(e.target));

  /* Back/forward cache: nothing to reset yet; motion libraries hook in here from S04. */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) document.dispatchEvent(new CustomEvent('antarti:restore'));
  });
})();

/* Theme editor: make the thing a founder just clicked in the sidebar visible on the canvas.
   Selecting a block opens its accordion / tab / dialog; selecting a section that owns a
   dialog marked data-editor-open (the bag drawer) opens it until deselected. */
(() => {
  'use strict';
  if (!window.Shopify?.designMode) return;

  const reveal = (el) => {
    if (!el || !(el instanceof Element)) return;
    /* A block can carry its attributes on a wrapper around its <details> (the product page's
       Specifications block, whose wrapper is swapped on variant change), so look one level down too. */
    const details = el.matches('details') ? el : el.closest('details') || el.querySelector(':scope > details');
    if (details) details.open = true;
    const dialog = el.closest('dialog');
    if (dialog && !dialog.open) dialog.showModal();
    const tab = el.matches('[role="tab"]') ? el : el.querySelector('[role="tab"]');
    if (tab) tab.click();
  };

  document.addEventListener('shopify:block:select', (e) => reveal(e.target));
  document.addEventListener('shopify:section:select', (e) => {
    const dialog = e.target.querySelector('dialog[data-editor-open]');
    if (dialog && !dialog.open) dialog.showModal();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const dialog = e.target.querySelector('dialog[data-editor-open][open]');
    if (dialog) dialog.close();
  });
})();
